import { ApiGatewayQueries } from "@/api/apiGateway/queries"
import { QueryResult } from "@/api/utils/types"
import { useQuery } from "@/api/utils/useQuery"
import { ImbpAvailableSlots } from "@/models/imbpAvailableSlots/imbpAvailableSlots"
import {
  IAvailableSlotsRaw,
  ImbpAvailableSlotsParser,
} from "@/models/imbpAvailableSlots/imbpAvailableSlots.parser"
import { ImbpBookingInformation } from "@/models/imbpBookingInformation/imbpBookingInformation"
import {
  IImbpBookingInformationRaw,
  ImbpBookingInformationParser,
} from "@/models/imbpBookingInformation/imbpBookingInformation.parser"
import { AggregatedQueryProps } from "@/api/utils/QueryAggregator"
import dayjs from "dayjs"
import { mapQueryResult } from "@/api/utils/mapQuery"
import { AppointmentType } from "@/constants/imbp"
import { isAfterToday } from "@/helpers/dateHelper"
import { ImbpSmartMeterInterest } from "@/models/imbpSmartMeterInterest/imbpSmartMeterInterest"
import { ImbpSmartMeterOfferedSlot } from "@/models/imbpSmartMeterOfferedSlots/imbpSmartMeterOfferedSlot"
import { ImbpSmartMeterOfferedSlotParser } from "@/models/imbpSmartMeterOfferedSlots/imbpSmartMeterOfferedSlot.parser"
import { BackofficeRestQueries } from "@/api/backofficeRest/queries"
import {
  OfferedAppointment,
  SmartMeterInterest,
  SmEligibilityReasonResponse,
} from "@/api/backofficeRest/generated/model"
import { ImbpSmartMeterInterestParser } from "@/models/imbpSmartMeterInterest/imbpSmartMeterInterest.parser"
import { SmartMeterEligibilityParser } from "@/models/smartMeterEligibility/smartMeterEligibility.parser"
import { SmartMeterEligibility } from "@/models/smartMeterEligibility/smartMeterEligibility"
import { IImbpCancellationReason } from "@/models/imbpCancellationReason/imbpCancellationReason"
import {
  IImbpCancellationReasonRaw,
  ImbpCancellationReasonParser,
} from "@/models/imbpCancellationReason/imbpCancellationReason.parser"

export interface IImbpAccountService {
  getAvailableSlots(
    postcode: string,
    billingAccountId?: string,
    agentEmail?: string,
    mop?: string,
    mprn?: string,
    mpan?: string,
    meterType?: string,
  ): QueryResult<ImbpAvailableSlots>
  getBookingInformation(
    billingAccountId: number,
  ): QueryResult<ImbpBookingInformation>
  hasUpcomingMeterExchange(
    billingAccountId: number,
  ): AggregatedQueryProps<boolean>
  getRegisteredSmartMeterInterest(
    billingAccountId: number,
  ): QueryResult<ImbpSmartMeterInterest>
  getPreviouslyOfferedSmartMeterSlots(
    billingAccountId: number,
  ): QueryResult<ImbpSmartMeterOfferedSlot[]>
  getSmartMeterEligibility(
    accountNumber: string,
  ): QueryResult<SmartMeterEligibility>
  getCancellationReasons(): QueryResult<IImbpCancellationReason[]>
}

class ImbpServiceClass implements IImbpAccountService {
  getAvailableSlots(
    postcode: string,
    billingAccountId?: string,
    agentEmail?: string,
    mop?: string,
    mprn?: string,
    mpan?: string,
    meterType?: string,
  ): QueryResult<ImbpAvailableSlots> {
    return useQuery({
      ...ApiGatewayQueries.imbp.availableSlots(
        postcode,
        billingAccountId,
        agentEmail,
        mop,
        mprn,
        mpan,
        meterType,
      ),
      parserFn: (rawData: IAvailableSlotsRaw) =>
        ImbpAvailableSlotsParser.toDomain(rawData),
      enabled: !!postcode && !!(mprn || mpan),
    })
  }
  getBookingInformation(
    billingAccountId: number | null,
  ): QueryResult<ImbpBookingInformation> {
    return useQuery({
      ...ApiGatewayQueries.imbp.bookingInformation(billingAccountId ?? -1),
      parserFn: (rawData: IImbpBookingInformationRaw) =>
        ImbpBookingInformationParser.toDomain(rawData),
      enabled: !!billingAccountId,
    })
  }
  hasUpcomingMeterExchange(
    billingAccountId: number,
  ): AggregatedQueryProps<boolean> {
    return mapQueryResult(
      this.getBookingInformation(billingAccountId),
      (query) =>
        !!query.data.bookings.filter(
          (booking) =>
            isAfterToday(dayjs(booking.slotStartDateTime)) &&
            booking.appointmentType === AppointmentType.METER_EXCHANGE &&
            ["booked", "rescheduled"].includes(booking.aesStatus.toLowerCase()),
        ).length,
    )
  }
  getRegisteredSmartMeterInterest(
    billingAccountId: number | null,
  ): QueryResult<ImbpSmartMeterInterest> {
    return useQuery({
      ...BackofficeRestQueries.smartMeter.getRegisteredInterest(
        billingAccountId?.toString() ?? "",
      ),
      enabled: !!billingAccountId,
      useRawResponse: true,
      parserFn: (response: SmartMeterInterest) => {
        if (!response) throw new Error("Failed to fetch smart meter interest")
        return ImbpSmartMeterInterestParser.toDomain(response)
      },
    })
  }
  getPreviouslyOfferedSmartMeterSlots(
    billingAccountId: number | null,
  ): QueryResult<ImbpSmartMeterOfferedSlot[]> {
    return useQuery({
      ...BackofficeRestQueries.smartMeter.getPreviouslyOfferedSlots(
        billingAccountId?.toString() ?? "",
      ),
      placeholderData: [] as ImbpSmartMeterOfferedSlot[],
      enabled: !!billingAccountId,
      useRawResponse: true,
      parserFn: (rawData: OfferedAppointment[]) =>
        (rawData ?? []).map((slot) =>
          ImbpSmartMeterOfferedSlotParser.toDomain(slot),
        ),
    })
  }

  getSmartMeterEligibility(
    accountNumber: string,
  ): QueryResult<SmartMeterEligibility> {
    return useQuery({
      ...BackofficeRestQueries.smartMeter.getSmartMeterEligibility(
        accountNumber,
      ),
      enabled: !!accountNumber,
      useRawResponse: true,
      parserFn: (rawData: SmEligibilityReasonResponse) => {
        if (!rawData) throw new Error("Failed to fetch smart meter eligibility")
        // An absent reason is a real state, not a failure: the parser maps it
        // to UNRECOGNIZED and the UI reads that as "not eligible".
        return SmartMeterEligibilityParser.toDomain(rawData)
      },
    })
  }

  getCancellationReasons(): QueryResult<IImbpCancellationReason[]> {
    return useQuery({
      ...ApiGatewayQueries.imbp.cancellationReasons(),
      placeholderData: [],
      parserFn: (rawData: IImbpCancellationReasonRaw[]) =>
        rawData.map(ImbpCancellationReasonParser.toDomain),
    })
  }
}

export const ImbpService = new ImbpServiceClass()
